// Dashboard.js - Main Component
import React, { useState, useEffect, useRef } from 'react';
import { FaPencil } from "react-icons/fa6";
import { RiArrowDropDownFill } from "react-icons/ri";
import { FaEdit } from "react-icons/fa";
import { LiaTruckPickupSolid } from "react-icons/lia";
import { TbBrandWindowsFilled } from "react-icons/tb";
import { MdOutlineQrCodeScanner, MdOutlineTaskAlt } from "react-icons/md";
import EventCalendar from './EventCalendar';
import AtAGlance from './AtAGlance';
import Users from './Users';
import EventDetailsModal from './EventDetailsModal';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
    const navigate = useNavigate();
  // User and Lab-specific states
  const [labId, setLabId] = useState(null);
  const [labName, setLabName] = useState('');
  const [userType, setUserType] = useState('admin');
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  
  // Lab selection states
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [selectedLabName, setSelectedLabName] = useState('');
  const [availableLabs, setAvailableLabs] = useState([]);
  const [showLabSelector, setShowLabSelector] = useState(false);
  
  // Data states
  const [labAccounts, setLabAccounts] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [labDataLoaded, setLabDataLoaded] = useState(false);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  // Modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSection, setSelectedSection] = useState('Cases');

  // Action dropdown state
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Action dropdown items
  const actionItems = [
    { name: 'New Case', icon: FaEdit, color: '#0e0f0fff' },
    { name: 'Complete Tasks', icon: MdOutlineTaskAlt, color: '#0e0f0fff' },
    { name: 'Location Scan', icon: MdOutlineQrCodeScanner, color: '#0e0f0fff' },
    { name: 'New Case Pickup', icon: LiaTruckPickupSolid, color: '#0e0f0fff' },
    { name: 'Driver UI', icon: LiaTruckPickupSolid, color: '#0e0f0fff' },
    { name: 'Lot Dispensing', icon: TbBrandWindowsFilled, color: '#0e0f0fff' }
  ];

  // Clear lab selection flag when dashboard mounts
  useEffect(() => {
    // Set flag to clear lab on dashboard
    localStorage.setItem('clearLabOnDashboard', 'true');
    // Dispatch event to notify Navbar
    window.dispatchEvent(new Event('labChanged'));
  }, []);

  // Load user and lab data from localStorage
  useEffect(() => {
    console.log('=== Loading User and Lab Data from localStorage ===');
    
    const loadUserData = () => {
      let storedUserId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      let storedUserName = localStorage.getItem('userName') || sessionStorage.getItem('userName');
      let storedUserEmail = localStorage.getItem('userEmail') || sessionStorage.getItem('userEmail');
      
      const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (!storedUserId && userData.id) storedUserId = userData.id;
          if (!storedUserName && (userData.name || userData.username)) storedUserName = userData.name || userData.username;
          if (!storedUserEmail && userData.email) storedUserEmail = userData.email;
        } catch (e) {
          console.error('Error parsing userData:', e);
        }
      }
      
      let storedLabId = localStorage.getItem('userLabId') || sessionStorage.getItem('userLabId');
      let storedLabName = localStorage.getItem('labName') || sessionStorage.getItem('labName');
      
      const labDataStr = localStorage.getItem('labData') || sessionStorage.getItem('labData');
      if (labDataStr) {
        try {
          const labData = JSON.parse(labDataStr);
          if (!storedLabId && labData.id) storedLabId = labData.id;
          if (!storedLabName && labData.name) storedLabName = labData.name;
        } catch (e) {
          console.error('Error parsing labData:', e);
        }
      }
      
      let storedUserType = localStorage.getItem('userType') || sessionStorage.getItem('userType') || 'admin';
      
      setUserId(storedUserId);
      setUserName(storedUserName || 'User');
      setUserEmail(storedUserEmail);
      setLabId(storedLabId);
      setLabName(storedLabName || '');
      setSelectedLabId(storedLabId);
      setSelectedLabName(storedLabName || 'My Lab');
      setUserType(storedUserType);
      
      setLabDataLoaded(true);
    };

    loadUserData();
  }, []);

  // Token validation and auto-logout effect - FIXED: Moved outside of the previous useEffect
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Check token expiry on mount
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const timeLeft = expiryTime - Date.now();

      if (timeLeft <= 0) {
        localStorage.clear();
        navigate('/login?sessionExpired=true');
      } else {
        // Auto logout when token expires
        const timer = setTimeout(() => {
          localStorage.clear();
          navigate('/login?sessionExpired=true');
        }, timeLeft);

        return () => clearTimeout(timer);
      }
    } catch (e) {
      localStorage.clear();
      navigate('/login');
    }
  }, [navigate]);

  // Fetch available labs
  useEffect(() => {
    if (!labDataLoaded || userType !== 'lab') return;

    const fetchAvailableLabs = async () => {
      try {
        try {
          const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/user/user-labs/${userId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.labs)) {
              setAvailableLabs([
                { id: 'all', name: 'All Labs', isAll: true },
                ...data.labs
              ]);
              return;
            }
          }
        } catch (error) {
          console.log('No labs API available, using default options');
        }
        
        createDefaultLabOptions();
      } catch (error) {
        console.error('Error fetching available labs:', error);
        createDefaultLabOptions();
      }
    };

    const createDefaultLabOptions = () => {
      const defaultLabs = [{ id: 'all', name: 'All Labs', isAll: true }];
      if (labId) {
        defaultLabs.push({ id: labId, name: labName || 'My Lab', isAll: false });
      }
      setAvailableLabs(defaultLabs);
    };

    if (userId && userType === 'lab') {
      fetchAvailableLabs();
    }
  }, [labDataLoaded, userId, userType, labId, labName]);

  // Fetch accounts based on selected lab
  useEffect(() => {
    if (!labDataLoaded) return;

    const fetchUserAndLabAccounts = async () => {
      try {
        console.log(`Dashboard: Fetching accounts for selected lab ${selectedLabId}...`);
        
        let userAccountsData = [];
        let labAccountsData = [];
        
        if (selectedLabId === 'all') {
          try {
            const allAccountsResponse = await fetch("https://hms.backend.hiragroupcloud.com/api/account/account");
            if (allAccountsResponse.ok) {
              const allData = await allAccountsResponse.json();
              if (allData.data && Array.isArray(allData.data)) {
                labAccountsData = allData.data;
              }
            }
          } catch (error) {
            console.error('Error fetching all accounts:', error);
          }
        } else if (userType !== 'admin' && userId && selectedLabId) {
          try {
            const userAccountsResponse = await fetch(`https://hms.backend.hiragroupcloud.com/api/account/user-accounts/${userId}`);
            if (userAccountsResponse.ok) {
              const userData = await userAccountsResponse.json();
              if (userData.success && userData.data) {
                userAccountsData = Array.isArray(userData.data) ? userData.data : [];
              }
            }
          } catch (error) {
            console.log('No user-specific accounts API available');
          }
          
          if (selectedLabId !== 'all') {
            try {
              const labAccountsResponse = await fetch(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${selectedLabId}`);
              if (labAccountsResponse.ok) {
                const labData = await labAccountsResponse.json();
                if (labData.success && labData.data) {
                  labAccountsData = Array.isArray(labData.data) ? labData.data : [];
                }
              }
            } catch (error) {
              console.error('Error fetching lab accounts:', error);
            }
          }
        } else if (userType === 'admin') {
          try {
            const allAccountsResponse = await fetch("https://hms.backend.hiragroupcloud.com/api/account/account");
            if (allAccountsResponse.ok) {
              const allData = await allAccountsResponse.json();
              if (allData.data && Array.isArray(allData.data)) {
                labAccountsData = allData.data;
              }
            }
          } catch (error) {
            console.error('Error fetching all accounts for admin:', error);
          }
        }
        
        let combinedAccounts = [...userAccountsData];
        if (labAccountsData.length > 0) {
          const userAccountIds = new Set(userAccountsData.map(acc => acc.accountId));
          labAccountsData.forEach(acc => {
            if (!userAccountIds.has(acc.accountId)) {
              combinedAccounts.push(acc);
            }
          });
        }
        
        setUserAccounts(userAccountsData);
        setLabAccounts(combinedAccounts);
        console.log('Total accounts available:', combinedAccounts.length);
        
      } catch (error) {
        console.error('Error in fetchUserAndLabAccounts:', error);
        setUserAccounts([]);
        setLabAccounts([]);
      }
    };

    fetchUserAndLabAccounts();
  }, [labDataLoaded, userId, userType, selectedLabId]);

  // Fetch all cases and filter
  useEffect(() => {
    if (!labDataLoaded) return;

    const fetchAllCases = async () => {
      try {
        setLoading(true);
        console.log('Fetching ALL cases data...');
        
        const response = await fetch("https://hms.backend.hiragroupcloud.com/api/cases/cases");
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const data = await response.json();
        
        let allCasesData = [];
        if (Array.isArray(data)) allCasesData = data;
        else if (data?.data && Array.isArray(data.data)) allCasesData = data.data;
        else if (data?.success && data?.data && Array.isArray(data.data)) allCasesData = data.data;
        
        console.log('Total cases from API:', allCasesData.length);
        setAllCases(allCasesData);
        
        let filteredData = [];
        if (userType === 'lab') {
          if (selectedLabId === 'all') {
            filteredData = allCasesData;
          } else if (labAccounts.length > 0) {
            const accountIds = labAccounts.map(acc => acc.accountId).filter(id => id);
            filteredData = allCasesData.filter(caseItem => {
              if (!caseItem?.accountId) return false;
              return accountIds.includes(caseItem.accountId);
            });
          }
        } else if (userType === 'admin') {
          filteredData = allCasesData;
        } else {
          if (userAccounts.length > 0) {
            const userAccountIds = userAccounts.map(acc => acc.accountId).filter(id => id);
            filteredData = allCasesData.filter(caseItem => {
              if (!caseItem?.accountId) return false;
              return userAccountIds.includes(caseItem.accountId);
            });
          }
        }
        
        setFilteredCases(filteredData);
        setLoading(false);
        
      } catch (error) {
        console.error("Error fetching cases data:", error);
        setAllCases([]);
        setFilteredCases([]);
        setLoading(false);
      }
    };

    fetchAllCases();
  }, [labDataLoaded, labAccounts, userAccounts, userType, selectedLabId, userId, userName]);

  // Fetch recent activities and alerts
  useEffect(() => {
    if (!labDataLoaded || filteredCases.length === 0) return;

    const fetchRecentActivities = async () => {
      try {
        setAlertsLoading(true);
        console.log('Fetching recent activities...');
        
        let recentData = [];
        
        try {
          let url = "https://hms.backend.hiragroupcloud.com/api/cases/recent-notes";
          if (userType === 'lab' && selectedLabId !== 'all') {
            url = `https://hms.backend.hiragroupcloud.com/api/cases/recent-notes/lab/${selectedLabId}`;
          } else if (userType !== 'admin' && selectedLabId !== 'all') {
            url = `https://hms.backend.hiragroupcloud.com/api/cases/recent-notes/user/${userId}`;
          }
          
          const response = await fetch(url);
          if (response.ok) {
            const notesData = await response.json();
            if (Array.isArray(notesData)) recentData = notesData;
            else if (notesData.success && Array.isArray(notesData.data)) recentData = notesData.data;
          }
        } catch (error) {
          console.log('No case notes API available, using filtered cases');
        }
        
        if (recentData.length === 0 && filteredCases.length > 0) {
          recentData = filteredCases.slice(0, 10).map((caseItem, index) => ({
            id: caseItem._id || index,
            case_id: caseItem.case_id || `CASE-${index + 1}`,
            patient_name: caseItem.last_name || caseItem.first_name || 'Patient',
            account_name: caseItem.account_name || caseItem.account_holder || 'Unknown',
            date: caseItem.date || caseItem.createdAt || new Date().toISOString(),
            has_note: true,
            note_type: 'internal'
          }));
        }
        
        const calculateTimeAgo = (dateString) => {
          if (!dateString) return 'Recently';
          try {
            const now = new Date();
            const past = new Date(dateString);
            if (isNaN(past.getTime())) return 'Recently';
            
            const diffMs = now - past;
            const diffMins = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            
            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
            if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            
            return new Date(dateString).toLocaleDateString();
          } catch (error) {
            return 'Recently';
          }
        };
        
        const formattedAlerts = recentData.map((activity) => {
          const timeAgo = calculateTimeAgo(activity.date);
          return {
            case: activity.case_id,
            name: activity.patient_name || activity.account_name,
            time: timeAgo,
            fullDate: activity.date,
            account: activity.account_name
          };
        });
        
        setAlerts(formattedAlerts);
        setRecentActivities(recentData);
        setAlertsLoading(false);
        
      } catch (error) {
        console.error("Error fetching recent activities:", error);
        setAlertsLoading(false);
      }
    };

    fetchRecentActivities();
  }, [labDataLoaded, filteredCases, userId, selectedLabId, userType]);

  // Calculate card counts
  const calculateCardCounts = () => {
    if (loading || !Array.isArray(filteredCases) || filteredCases.length === 0) {
      return {
        'Cases Arriving': 0,
        'Cases Shipping': 0,
        'Cases On Hold': 0,
        'Open Try-in Cases': 0,
        'Pickups': 0,
        'Deliveries': 0,
        'Pre-Invoiced Cases': 0,
        'New Accounts': 0
      };
    }

    const formatDateToYYYYMMDD = (date) => {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const todayString = formatDateToYYYYMMDD(today);
    
    return {
      'Cases Arriving': filteredCases.filter(c => {
        if (!c?.arrive_date) return false;
        return formatDateToYYYYMMDD(c.arrive_date) === todayString;
      }).length,
      
      'Cases Shipping': filteredCases.filter(c => {
        if (!c?.delivery_date) return false;
        return formatDateToYYYYMMDD(c.delivery_date) === todayString;
      }).length,
      
      'Cases On Hold': filteredCases.filter(c => 
        c && (c.status === 'hold' || c.status === 'on hold' || c.status === 'Hold')
      ).length,
      
      'Open Try-in Cases': filteredCases.filter(c => 
        c && ((c.case_type && c.case_type.toLowerCase().includes('try-in')) || 
              (c.workRequested && c.workRequested.toLowerCase().includes('try-in'))) && 
        c.status !== 'completed' && c.status !== 'delivered'
      ).length,
      
      'Pickups': filteredCases.filter(c => {
        if (!c?.pickup_date) return false;
        return formatDateToYYYYMMDD(c.pickup_date) === todayString;
      }).length,
      
      'Deliveries': filteredCases.filter(c => {
        if (!c?.delivery_date) return false;
        return formatDateToYYYYMMDD(c.delivery_date) === todayString;
      }).length,
      
      'Pre-Invoiced Cases': filteredCases.filter(c => 
        c && (c.invoice_status === 'pre-invoiced' || 
              c.billing_status === 'pre-invoiced' ||
              (c.status && c.status.toLowerCase().includes('pre-invoiced')))
      ).length,
      
      'New Accounts': 0
    };
  };

  const cardCounts = calculateCardCounts();

  // Handlers
  const handleLabSelect = (lab) => {
    setSelectedLabId(lab.id);
    setSelectedLabName(lab.name);
    setShowLabSelector(false);
    setLoading(true);
    setFilteredCases([]);
    setAlerts([]);
    setRecentActivities([]);
  };

  const handleActionSelect = (item) => {
    console.log(`Selected: ${item.name}`);
    setActionDropdownOpen(false);
  };

  const handleEventClick = (day, section) => {
    setSelectedEvent(day);
    setSelectedSection(section);
    setShowEventModal(true);
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setSelectedSection('Cases');
  };

  // Close action dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideAction = (event) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideAction);
    return () => document.removeEventListener('mousedown', handleClickOutsideAction);
  }, []);

  // Close lab selector when clicking outside
  useEffect(() => {
    const handleClickOutsideLabSelector = (event) => {
      if (!event.target.closest('.lab-selector-container')) {
        setShowLabSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideLabSelector);
    return () => document.removeEventListener('mousedown', handleClickOutsideLabSelector);
  }, []);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      padding: isMobile ? '1rem' : '2rem',
      overflowX: 'hidden',
      position: 'relative'
    }}>
      <EventDetailsModal
        isOpen={showEventModal}
        onClose={handleCloseModal}
        eventData={selectedEvent}
        selectedSection={selectedSection}
        filteredCases={filteredCases}
        userType={userType}
        labId={selectedLabId === 'all' ? null : selectedLabId}
        labName={selectedLabName}
        userName={userName}
      />
    
      {/* HEADER */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        marginBottom: isMobile ? '1rem' : '2rem',
        gap: isMobile ? '1rem' : '0',
      }}>
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '0.75rem' : '1rem'
        }}>
     
          {/* Lab Selector */}
          {userType === 'lab' && availableLabs.length > 0 && (
            <div className="lab-selector-container" style={{ 
              position: 'relative', 
              display: 'inline-block',
              alignSelf: isMobile ? 'stretch' : 'flex-start'
            }}>
              <button
                onClick={() => setShowLabSelector(!showLabSelector)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  background: '#f8fafc',
                  color: '#1e293b',
                  border: '2px solid #cbd5e1',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: isMobile ? '100%' : '250px',
                  minHeight: '44px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    color: selectedLabId === 'all' ? '#004aad' : '#1e293b',
                    fontWeight: selectedLabId === 'all' ? '700' : '600'
                  }}>
                    {selectedLabName || 'Select Lab'}
                  </span>
                  {selectedLabId === 'all' && (
                    <span style={{
                      fontSize: '0.7rem',
                      color: '#64748b',
                      backgroundColor: '#e6f7ff',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      All Data
                    </span>
                  )}
                </div>
                <RiArrowDropDownFill
                  size={24}
                  color="#64748b"
                  style={{
                    transition: 'transform 0.2s ease',
                    transform: showLabSelector ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </button>

              {showLabSelector && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#f8fafc',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #e2e8f0',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: '4px'
                }}>
                  {availableLabs.map((lab, index) => (
                    <div
                      key={index}
                      onClick={() => handleLabSelect(lab)}
                      style={{
                        padding: '12px 16px',
                        color: lab.isAll ? '#004aad' : '#1e293b',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: selectedLabId === lab.id ? '#e6f7ff' : 'transparent',
                        borderBottom: index < availableLabs.length - 1 ? '1px solid #e2e8f0' : 'none',
                        fontWeight: lab.isAll ? '700' : selectedLabId === lab.id ? '600' : '400',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLabId !== lab.id) {
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLabId !== lab.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span>{lab.name}</span>
                      {lab.isAll && (
                        <span style={{
                          fontSize: '0.7rem',
                          color: '#64748b',
                          backgroundColor: '#e6f7ff',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          All Data
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Status Badge */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '0.5rem' : '0.75rem',
            flexWrap: 'wrap'
          }}>
            <span style={{
              fontSize: '0.85rem',
              fontWeight: '500',
              color: '#64748b',
              backgroundColor: '#f1f5f9',
              padding: '6px 12px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {userType === 'lab' ? (
                selectedLabId === 'all' ? (
                  <>
                    <span style={{ color: '#004aad', fontWeight: '600' }}>All Labs</span>
                    <span>|</span>
                    <span>Cases: {filteredCases.length}</span>
                  </>
                ) : (
                  <>
                    <span>Lab:</span>
                    <span style={{ color: '#004aad', fontWeight: '600' }}>{selectedLabName || selectedLabId}</span>
                    <span>|</span>
                    <span>Cases: {filteredCases.length}</span>
                  </>
                )
              ) : userType === 'admin' ? (
                <>
                  <span style={{ color: '#004aad', fontWeight: '600' }}>Admin View</span>
                  <span>|</span>
                  <span>Total Cases: {filteredCases.length}</span>
                </>
              ) : (
                <>
                  <span style={{ color: '#004aad', fontWeight: '600' }}>User View</span>
                  <span>|</span>
                  <span>My Cases: {filteredCases.length}</span>
                </>
              )}
            </span>
            
            {loading && (
              <span style={{
                fontSize: '0.8rem',
                color: '#ffffff',
                backgroundColor: '#f59e0b',
                padding: '6px 12px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#ffffff',
                  borderRadius: '50%',
                  animation: 'pulse 1.5s infinite'
                }}></span>
                Loading data...
              </span>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div ref={actionDropdownRef} style={{
          position: 'relative',
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-end'
        }}>
          <button
            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: '#004aad',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: isMobile ? '80%' : '140px',
              position: 'relative',
            }}
          >
            <FaPencil size={isMobile ? 16 : 18} color="white" />
            <span>Actions</span>
            <RiArrowDropDownFill
              size={isMobile ? 20 : 30}
              color="white"
              style={{
                transition: 'transform 0.2s ease',
                transform: actionDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </button>

          {actionDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              background: '#f4f4f4',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e2e8f0',
              zIndex: 800,
              minWidth: isMobile ? '100%' : '170px',
              overflow: 'hidden',
              marginTop: '4px'
            }}>
              {actionItems.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleActionSelect(item)}
                  style={{
                    padding: '10px 8px',
                    color: '#1e293b',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ddd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}>
                    <item.icon size={18} color={item.color} />
                  </div>
                  <span style={{ flex: 1, fontWeight: '500' }}>{item.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event Calendar Component */}
      <EventCalendar
        filteredCases={filteredCases}
        loading={loading}
        userType={userType}
        selectedLabId={selectedLabId}
        selectedLabName={selectedLabName}
        userName={userName}
        onEventClick={handleEventClick}
      />

      {/* Main Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '1rem' : '1.5rem',
        marginTop: isMobile ? '0.5rem' : '1rem',
        position: 'relative'
      }}>
        {/* At a Glance Component */}
        <AtAGlance
          cardCounts={cardCounts}
          loading={loading}
          userType={userType}
          selectedLabId={selectedLabId}
          selectedLabName={selectedLabName}
          userName={userName}
        />

        {/* Alerts & Activity Component */}
        <Users
          alerts={alerts}
          recentActivities={recentActivities}
          alertsLoading={alertsLoading}
          userType={userType}
          selectedLabId={selectedLabId}
          selectedLabName={selectedLabName}
          userName={userName}
          filteredCases={filteredCases}
        />
      </div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;